import React from 'react';
import { formatCustomerName, decodeSlashes } from '../../../utility/formatCustomerName';
import css from './BreadcrumbNav.module.css';

interface BreadcrumbNavProps {
  currentPath: string;
  onPathChange: (path: string) => void;
  maxVisibleFolders?: number;
}

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({ 
  currentPath, 
  onPathChange,
  maxVisibleFolders = 2 // Default to 2 visible folders
}) => {
  const handleRootClick = () => {
    onPathChange('');
  };

  const handleBreadcrumbClick = (index: number, parts: string[]) => {
    const newPath = parts.slice(0, index + 1).join('/');
    // Add trailing slash to match the up navigation behavior
    const finalPath = newPath ? `${newPath}/` : '';
    onPathChange(finalPath);
  };

  const handleEllipsisClick = () => {
    // When ellipsis is clicked, show a folder selection dialog or
    // navigate to an intermediate folder
    // First decode the current path to work with it properly
    const pathWithPreservedSlashes = currentPath.replace(/%2F/g, 'REPLACINGSLASHES');
    const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
    const pathParts = decodedPath.split('/').filter(Boolean);
    
    const midPoint = Math.floor(pathParts.length / 2);
    const midPath = pathParts.slice(0, midPoint).join('/');
    // Add trailing slash to match the up navigation behavior
    const finalPath = midPath ? `${midPath}/` : '';
    onPathChange(finalPath);
  };

  // Format a folder name - use "Stage X" for numeric names
  const formatFolderName = (part: string, index: number): string => {
    // First decode any encoded slashes and %2F
    const decodedPart = decodeURIComponent(part);
    
    // Replace REPLACINGSLASHES with / for display
    const displayPart = decodedPart.replace(/REPLACINGSLASHES/g, '/');
    
    if (index === 0) {
      return formatCustomerName(displayPart);
    } else if (/^\d+$/.test(displayPart.trim())) {
      return `Stage ${displayPart}`;
    }
    return displayPart;
  };

  const renderBreadcrumbs = () => {
    // First decode the current path to work with it properly
    // Replace %2F with REPLACINGSLASHES to preserve them during decoding
    const pathWithPreservedSlashes = currentPath.replace(/%2F/g, 'REPLACINGSLASHES');
    const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
    
    // Split the decoded path into parts
    const pathParts = decodedPath.split('/').filter(Boolean);
    
    // If we have fewer parts than max + 1, just show all of them
    if (pathParts.length <= maxVisibleFolders + 1) {
      return pathParts.map((part, index, array) => (
        <span key={index} className={css.breadcrumbWrapper}>
          <span className={css.breadcrumbSeparator}>/</span>
          <button
            onClick={() => handleBreadcrumbClick(index, array)}
            className={css.breadcrumbItem}
          >
            {formatFolderName(part, index)}
          </button>
        </span>
      ));
    }
    
    // Otherwise show first, ellipsis, and last folders
    return (
      <>
        {/* First folder */}
        <span className={css.breadcrumbWrapper}>
          <span className={css.breadcrumbSeparator}>/</span>
          <button
            onClick={() => handleBreadcrumbClick(0, pathParts)}
            className={css.breadcrumbItem}
          >
            {formatFolderName(pathParts[0], 0)}
          </button>
        </span>
        
        {/* Ellipsis for hidden folders */}
        <span className={css.breadcrumbWrapper}>
          <span className={css.breadcrumbSeparator}>/</span>
          <button
            onClick={handleEllipsisClick}
            className={`${css.breadcrumbItem} ${css.ellipsis}`}
          >
            ...
          </button>
        </span>
        
        {/* Last folder(s) based on maxVisibleFolders */}
        {pathParts.slice(-maxVisibleFolders).map((part, index) => {
          const actualIndex = pathParts.length - maxVisibleFolders + index;
          return (
            <span key={actualIndex} className={css.breadcrumbWrapper}>
              <span className={css.breadcrumbSeparator}>/</span>
              <button
                onClick={() => handleBreadcrumbClick(actualIndex, pathParts)}
                className={css.breadcrumbItem}
              >
                {formatFolderName(part, actualIndex)}
              </button>
            </span>
          );
        })}
      </>
    );
  };

  return (
    <div className={css.breadcrumb}>
      <button 
        onClick={handleRootClick}
        className={css.breadcrumbItem}
      >
        All
      </button>
      
      {currentPath && renderBreadcrumbs()}
    </div>
  );
};

export default BreadcrumbNav;